import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { Location } from '@freelancer/location';
import { take } from 'rxjs/operators';
import { SITE_WIDE_LINK_TAGS } from './link-tags.config';
import { LinkTag, SiteWideLinkTags } from './link-tags.interface';

/**
 * LinkTags is a service that allows the insertion of links into the HEAD section
 * of a page.
 */

@Injectable({
  providedIn: 'root',
})
export class LinkTags {
  constructor(
    private location: Location,
    @Inject(DOCUMENT) private doc: Document,
    @Optional()
    @Inject(SITE_WIDE_LINK_TAGS)
    private siteWideConfig: SiteWideLinkTags,
  ) {}

  /**
   * Adds a link to the HEAD section of the current page.
   */
  addLink(tag: LinkTag): void {
    const link = this.doc.createElement('link');

    if (tag.rel) {
      link.setAttribute('rel', tag.rel);
    }

    if (tag.href) {
      link.setAttribute('href', tag.href);
    }

    if (tag.itemprop) {
      link.setAttribute('itemprop', tag.itemprop);
    }

    if (tag.title) {
      link.setAttribute('title', tag.title);
    }

    if (tag.type) {
      link.setAttribute('type', tag.type);
    }

    this.doc.head.appendChild(link);
  }

  /**
   * Sets the link tags that will be used on all pages using the site wide
   * link tags configuration.
   */
  setSiteWideLinks(): void {
    if (this.siteWideConfig) {
      Object.values(this.siteWideConfig).forEach(linkTag => {
        this.addLink(linkTag);
      });
    }
  }

  /**
   * Sets the canonical link for the current page.
   */
  setCanonicalLink(href: string): void {
    // Canonical url tells search engines that the specific url is the master
    // copy of that page.
    this.location
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then(location => {
        this.addLink({ rel: 'canonical', href: `${location.origin}${href}` });
      });
  }
}
